import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  HISTORY_PAGE_SIZE,
  collectHistoryTerms,
  historyPageStart,
  nextHistoryPageStart,
  parseHistoryPayload,
  previousHistoryPageStart,
  trimHistoryPages,
  type HistoryPage,
} from "../lib/history";
import type { WorldTerm } from "../lib/world";

interface HistoryRetry {
  start: bigint;
  through: bigint;
  reset: boolean;
}

interface UseHistoryArchiveResult {
  terms: WorldTerm[];
  through: bigint;
  followingLatest: boolean;
  newCount: bigint;
  isBusy: boolean;
  isInitialLoading: boolean;
  error: string | null;
  hasOlder: boolean;
  hasNewer: boolean;
  loadedPageCount: number;
  loadOlder: () => Promise<void>;
  loadNewer: () => Promise<void>;
  jumpTo: (n: bigint) => Promise<void>;
  goFirst: () => Promise<void>;
  goLatest: () => Promise<void>;
  stopFollowing: () => void;
  retry: () => Promise<void>;
}

export function useHistoryArchive(
  liveTerm: WorldTerm,
  liveTerms: readonly WorldTerm[],
): UseHistoryArchiveResult {
  const initialThrough = BigInt(liveTerm.n);
  const [snapshotThrough, setSnapshotThrough] = useState(initialThrough);
  const [followingLatest, setFollowingLatest] = useState(true);
  const [pages, setPages] = useState<Map<string, HistoryPage>>(() => new Map());
  const [loadingStarts, setLoadingStarts] = useState<Set<string>>(() => new Set());
  const [error, setError] = useState<string | null>(null);
  const activeRef = useRef(true);
  const generationRef = useRef(0);
  const controllersRef = useRef(new Map<string, AbortController>());
  const inflightRef = useRef(new Map<string, Promise<void>>());
  const retryRef = useRef<HistoryRetry | null>(null);
  const liveN = BigInt(liveTerm.n);
  const through = followingLatest && liveN > snapshotThrough ? liveN : snapshotThrough;

  useEffect(() => {
    activeRef.current = true;
    const controllers = controllersRef.current;
    const inflight = inflightRef.current;
    return () => {
      activeRef.current = false;
      generationRef.current += 1;
      for (const controller of controllers.values()) controller.abort();
      controllers.clear();
      inflight.clear();
    };
  }, []);

  const loadPage = useCallback(async (
    start: bigint,
    requestThrough: bigint,
    reset: boolean,
  ): Promise<void> => {
    if (reset) {
      generationRef.current += 1;
      for (const controller of controllersRef.current.values()) controller.abort();
      controllersRef.current.clear();
      inflightRef.current.clear();
      setPages(new Map());
      setLoadingStarts(new Set());
    }

    const startKey = start.toString();
    const generation = generationRef.current;
    const key = `${generation}:${startKey}`;
    const existing = inflightRef.current.get(key);
    if (existing) return existing;

    setError(null);
    setLoadingStarts((current) => new Set(current).add(key));
    const controller = new AbortController();
    controllersRef.current.set(key, controller);

    const promise = (async () => {
      try {
        const query = new URLSearchParams({
          from: startKey,
          limit: HISTORY_PAGE_SIZE.toString(),
          through: requestThrough.toString(),
        });
        const response = await fetch(`/api/history?${query}`, {
          signal: controller.signal,
          headers: { Accept: "application/json" },
        });
        if (!response.ok) throw new Error(`history request failed (${response.status})`);

        const payload = parseHistoryPayload(await response.json());
        if (payload.from !== startKey) throw new Error("history response started at an unexpected term");
        if (!activeRef.current || generation !== generationRef.current) return;

        const page: HistoryPage = { start: startKey, terms: payload.terms };
        setPages((current) => {
          const next = reset ? new Map<string, HistoryPage>() : new Map(current);
          next.set(startKey, page);
          return trimHistoryPages(next, start);
        });
        retryRef.current = null;
      } catch (caught) {
        if (controller.signal.aborted || !activeRef.current) return;
        console.warn("history_page_load_failed", {
          from: startKey,
          error: caught instanceof Error ? caught.message : String(caught),
        });
        if (generation !== generationRef.current) return;
        retryRef.current = { start, through: requestThrough, reset };
        setError("履歴を読み込めませんでした。接続を確認して再試行してください。");
      } finally {
        if (controllersRef.current.get(key) === controller) controllersRef.current.delete(key);
        inflightRef.current.delete(key);
        if (activeRef.current && generation === generationRef.current) {
          setLoadingStarts((current) => {
            const next = new Set(current);
            next.delete(key);
            return next;
          });
        }
      }
    })();

    inflightRef.current.set(key, promise);
    return promise;
  }, []);

  useEffect(() => {
    if (!followingLatest) return;
    const start = historyPageStart(through);
    if (pages.has(start.toString())) return;
    let cancelled = false;
    queueMicrotask(() => {
      if (!cancelled) void loadPage(start, through, false);
    });
    return () => {
      cancelled = true;
    };
  }, [followingLatest, loadPage, pages, through]);

  const terms = useMemo(
    () => collectHistoryTerms(pages, through, liveTerms, followingLatest),
    [followingLatest, liveTerms, pages, through],
  );

  const pageBounds = useMemo(() => {
    const starts = [...pages.keys()].map(BigInt);
    if (starts.length === 0) return null;
    return {
      oldest: starts.reduce((minimum, value) => value < minimum ? value : minimum),
      newest: starts.reduce((maximum, value) => value > maximum ? value : maximum),
    };
  }, [pages]);

  const loadOlder = useCallback(async () => {
    const starts = [...pages.keys()].map(BigInt);
    if (starts.length === 0) return;
    const oldest = starts.reduce((minimum, value) => value < minimum ? value : minimum);
    const previous = previousHistoryPageStart(oldest);
    if (previous !== null) await loadPage(previous, through, false);
  }, [loadPage, pages, through]);

  const loadNewer = useCallback(async () => {
    const starts = [...pages.keys()].map(BigInt);
    if (starts.length === 0) return;
    const newest = starts.reduce((maximum, value) => value > maximum ? value : maximum);
    const next = nextHistoryPageStart(newest, through);
    if (next !== null) await loadPage(next, through, false);
  }, [loadPage, pages, through]);

  const stopFollowing = useCallback(() => {
    if (!followingLatest) return;
    const frozenThrough = BigInt(liveTerm.n);
    setSnapshotThrough(frozenThrough);
    setFollowingLatest(false);
  }, [followingLatest, liveTerm.n]);

  const jumpTo = useCallback(async (n: bigint) => {
    const latest = BigInt(liveTerm.n);
    if (n >= latest) {
      const nextThrough = latest;
      setSnapshotThrough(nextThrough);
      setFollowingLatest(true);
      await loadPage(historyPageStart(nextThrough), nextThrough, true);
      return;
    }

    const requestThrough = n > through ? latest : through;
    setSnapshotThrough(requestThrough);
    setFollowingLatest(false);
    await loadPage(historyPageStart(n), requestThrough, true);
  }, [liveTerm.n, loadPage, through]);

  const goFirst = useCallback(() => jumpTo(0n), [jumpTo]);
  const goLatest = useCallback(async () => {
    const latest = BigInt(liveTerm.n);
    setSnapshotThrough(latest);
    setFollowingLatest(true);
    await loadPage(historyPageStart(latest), latest, true);
  }, [liveTerm.n, loadPage]);

  const retry = useCallback(async () => {
    const pending = retryRef.current;
    if (!pending) return;
    await loadPage(pending.start, pending.through, pending.reset);
  }, [loadPage]);

  const latestN = BigInt(liveTerm.n);
  const newCount = latestN > through ? latestN - through : 0n;
  const latestPage = historyPageStart(through);

  return {
    terms,
    through,
    followingLatest,
    newCount,
    isBusy: loadingStarts.size > 0,
    isInitialLoading: loadingStarts.size > 0 && pages.size === 0,
    error,
    hasOlder: pageBounds !== null && pageBounds.oldest > 1n,
    hasNewer: pageBounds !== null && pageBounds.newest < latestPage,
    loadedPageCount: pages.size,
    loadOlder,
    loadNewer,
    jumpTo,
    goFirst,
    goLatest,
    stopFollowing,
    retry,
  };
}
